import {
  collection,
  doc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase/firebase";

export const CATEGORIES = [
  "Regular Member",
  "Associate Member",
  "Student Member",
  "Affiliate Member",
  "Corporate Member",
];
export const APPROVALS = ["Not recorded", "Pending", "Approved", "Rejected"];
export const STATUSES = ["Active", "Inactive"];
export const PAYMENT_METHODS = ["M-Pesa", "Bank transfer", "Cash", "Other"];

/* helpers */

const actor = () => auth.currentUser?.email || "unknown";

export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value) {
  const d = toDate(value);
  return d
    ? d.toLocaleDateString("en-KE", { day: "2-digit", month: "short", year: "numeric" })
    : "—";
}

export function formatDateTime(value) {
  const d = toDate(value);
  return d
    ? d.toLocaleString("en-KE", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
}

export function initials(name = "") {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join("") || "?"
  );
}

export function periodOptions() {
  const now = new Date().getFullYear();
  const years = [];
  for (let y = now + 1; y >= 2023; y--) years.push(String(y));
  return years;
}

const capitalise = (s = "") =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

function splitName(full = "") {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") };
}

function normalizeMember(id, d) {
  let firstName = d.firstName || "";
  let lastName = d.secondName || d.lastName || "";
  if (!firstName && !lastName) {
    ({ firstName, lastName } = splitName(d.fullName || d.name || ""));
  }
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "Unnamed member";
  const rawStatus = (d.status || "").trim();
  return {
    id,
    firstName,
    lastName,
    surnameField: d.secondName ? "secondName" : "lastName",
    fullName,
    email: d.email || "",
    phone: d.phone || d.phoneNumber || "",
    memberNumber: d.membershipNumber || d.memberNumber || "",
    category: d.category || d.membershipType || d.membershipCategory || "",
    organization: d.organization || d.organisation || d.institution || "",
    approval: d.approval ? capitalise(d.approval) : "Not recorded",
    status: rawStatus ? capitalise(rawStatus) : "Inactive",
    certificate: String(d.certificate || "").toLowerCase() === "issued" ? "Issued" : "Not recorded",
    createdAt: d.createdAt || null,
  };
}

function normalizeApplication(id, d) {
  let firstName = d.firstName || "";
  let lastName = d.secondName || d.lastName || "";
  if (!firstName && !lastName) {
    ({ firstName, lastName } = splitName(d.fullName || d.name || ""));
  }
  return {
    id,
    firstName,
    lastName,
    fullName: [firstName, lastName].filter(Boolean).join(" ") || "Unnamed applicant",
    email: d.email || "",
    phone: d.phone || d.phoneNumber || "",
    category: d.category || d.membershipType || d.membershipCategory || "",
    organization: d.organization || d.organisation || d.institution || "",
    createdAt: d.createdAt || d.submittedAt || d.timestamp || null,
    registered: !!d.registered,
    dismissed: !!d.dismissed,
    raw: d,
  };
}

/* activity log */

export async function logActivity(type, message, memberId = null) {
  try {
    await addDoc(collection(db, "activityLog"), {
      type,
      message,
      memberId,
      actor: actor(),
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn("Could not write activity log", err);
  }
}

export async function fetchActivity(max = 50) {
  const snap = await getDocs(
    query(collection(db, "activityLog"), orderBy("createdAt", "desc"), limit(max))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/* members */

export async function fetchMembers() {
  const snap = await getDocs(collection(db, "members"));
  return snap.docs.map((d) => normalizeMember(d.id, d.data()));
}

export async function createMember(data, label, extra = {}) {
  const ref = await addDoc(collection(db, "members"), {
    ...data,
    ...extra,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await logActivity("member", `Added member ${label}`, ref.id);
  return ref.id;
}

export async function updateMember(id, data, label) {
  await updateDoc(doc(db, "members", id), { ...data, updatedAt: serverTimestamp() });
  await logActivity("member", `Updated member ${label}`, id);
}

export async function deleteMember(member) {
  const batch = writeBatch(db);
  const pays = await getDocs(query(collection(db, "payments"), where("memberId", "==", member.id)));
  pays.forEach((p) => batch.delete(p.ref));
  batch.delete(doc(db, "members", member.id));
  await batch.commit();
  await logActivity("member", `Deleted member ${member.fullName}`, member.id);
}

/*  payments (one doc per member per period)  */

export async function fetchPaymentsForPeriod(period) {
  const snap = await getDocs(query(collection(db, "payments"), where("period", "==", String(period))));
  const map = {};
  snap.forEach((d) => {
    const data = d.data();
    map[data.memberId] = { id: d.id, ...data };
  });
  return map;
}

export async function savePayment(member, period, data) {
  await setDoc(
    doc(db, "payments", `${member.id}_${period}`),
    {
      memberId: member.id,
      memberName: member.fullName,
      period: String(period),
      status: data.status,
      amount: Number(data.amount) || 0,
      method: data.method || "",
      reference: data.reference || "",
      paidOn: data.paidOn || "",
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  await logActivity(
    "payment",
    `${member.fullName}: ${period} marked ${data.status}${
      data.status === "paid" && data.amount ? ` (KES ${Number(data.amount).toLocaleString()})` : ""
    }`,
    member.id
  );
}

/* website submissions */

export async function fetchApplications() {
  const snap = await getDocs(collection(db, "membershipApplications"));
  return snap.docs
    .map((d) => normalizeApplication(d.id, d.data()))
    .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0));
}

export async function registerApplication(app) {
  const memberId = await createMember(
    {
      firstName: app.firstName,
      lastName: app.lastName,
      email: app.email,
      phone: app.phone,
      category: app.category,
      organization: app.organization,
      approval: "Pending",
      status: "Inactive",
      certificate: "Not recorded",
      membershipNumber: "",
    },
    app.fullName,
    { sourceApplicationId: app.id }
  );
  await updateDoc(doc(db, "membershipApplications", app.id), { registered: true, memberId });
  await logActivity("submission", `Registered application from ${app.fullName}`, memberId);
  return memberId;
}

export async function dismissApplication(app) {
  await updateDoc(doc(db, "membershipApplications", app.id), {
    dismissed: true,
    dismissedBy: actor(),
    dismissedAt: serverTimestamp(),
  });
  await logActivity("submission", `Dismissed application from ${app.fullName}`);
}

/* shop orders */

const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");
const text = (v) => (v === undefined || v === null || typeof v === "object" ? "" : String(v));

export const ORDER_STATUSES = [
  ["new", "New"],
  ["processing", "Processing"],
  ["fulfilled", "Fulfilled"],
  ["cancelled", "Cancelled"],
];

export function normalizeOrder(id, d) {
  const cust = d.customer || d.buyer || {};
  const nameParts = [d.firstName, d.lastName].filter(Boolean).join(" ");
  const rawItems = pick(d.items, d.cart, d.products, d.orderItems, d.lineItems);
  const itemsArr = Array.isArray(rawItems)
    ? rawItems
    : rawItems && typeof rawItems === "object"
    ? Object.values(rawItems)
    : [];
  const items = itemsArr.map((it) => {
    if (typeof it === "string") return { name: it, qty: 1, price: 0, opts: "" };
    const qty = Number(pick(it.quantity, it.qty, it.count, 1)) || 1;
    const price = Number(pick(it.price, it.unitPrice, it.amount, 0)) || 0;
    const opts = [it.size, it.color, it.colour, it.variant].filter(Boolean).join(", ");
    return { name: text(pick(it.name, it.title, it.productName, it.product, "Item")), qty, price, opts };
  });
  const computed = items.reduce((s, it) => s + it.qty * it.price, 0);
  const total = Number(pick(d.total, d.totalAmount, d.grandTotal, d.amount, d.totalPrice, computed)) || 0;

  const a = pick(d.address, d.deliveryAddress, d.shippingAddress, d.delivery, d.location);
  const address =
    typeof a === "string"
      ? a
      : a && typeof a === "object"
      ? Object.values(a).filter((v) => typeof v === "string" && v).join(", ")
      : "";

  return {
    id,
    orderNo: text(pick(d.orderNumber, d.orderNo, d.orderId, d.reference)),
    customer: text(pick(d.customerName, d.fullName, d.name, nameParts, cust.name, cust.fullName)) || "Unnamed customer",
    email: text(pick(d.email, d.customerEmail, cust.email)),
    phone: text(pick(d.phone, d.phoneNumber, d.customerPhone, cust.phone)),
    address,
    notes: text(pick(d.notes, d.note, d.message, d.comments)),
    items,
    total,
    currency: text(pick(d.currency, "KES")),
    status: d.adminStatus || "new",
    createdAt: pick(d.createdAt, d.orderDate, d.timestamp, d.submittedAt, d.date) || null,
    raw: d,
  };
}

export async function setOrderStatus(order, status) {
  await updateDoc(doc(db, "merchOrders", order.id), {
    adminStatus: status,
    adminUpdatedBy: actor(),
    adminUpdatedAt: serverTimestamp(),
  });
  await logActivity("order", `Order from ${order.customer} marked ${status}`);
}

/* live subscriptions */

function subscribeCollection(name, normalize, onData, onError) {
  return onSnapshot(
    collection(db, name),
    (snap) => {
      const rows = snap.docs.map((d) => normalize(d.id, d.data()));
      rows.sort((x, y) => (toDate(y.createdAt)?.getTime() || 0) - (toDate(x.createdAt)?.getTime() || 0));
      const added = snap.docChanges().filter((c) => c.type === "added").map((c) => c.doc.id);
      onData(rows, added);
    },
    onError
  );
}

export const subscribeApplications = (onData, onError) =>
  subscribeCollection("membershipApplications", normalizeApplication, onData, onError);

export const subscribeOrders = (onData, onError) =>
  subscribeCollection("merchOrders", normalizeOrder, onData, onError);
